import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Truck, CheckCircle2, AlertOctagon, Plus } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, LabelList, Cell } from "recharts";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

const BRAND_COLORS = {
  "Natura": "#F26522", "O Boticário": "#005542", "Avon": "#E10078",
  "Eudora": "#5B2C6F", "Quem Disse Berenice": "#E83A82", "Wepink": "#FF4081", "Outras": "#5A6B62",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/ocorrencias/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) {
    return <div className="py-20 text-center text-[#5A6B62]" data-testid="dashboard-loading">Carregando indicadores...</div>;
  }

  const kpis = [
    { id: "dashboard-kpi-pendentes", title: "Pendentes de Triagem", value: stats.pendentes, icon: Clock, color: "#B8860B", sub: "Aguard. espelho + GDV" },
    { id: "dashboard-kpi-em-analise", title: "Em Trânsito", value: stats.em_transito, icon: Truck, color: "#2563EB", sub: "Coleta em curso" },
    { id: "dashboard-kpi-resolvidas", title: "Finalizadas", value: stats.finalizado, icon: CheckCircle2, color: "#059669", sub: "Fluxo concluído" },
    { id: "dashboard-kpi-criticas", title: "Críticas / Insucesso", value: stats.criticas, icon: AlertOctagon, color: "#DC2626", sub: "Insucesso de coleta" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2A1F]">Dashboard Ocorrências</h1>
          <p className="text-sm text-[#5A6B62] mt-1">Indicadores críticos da operação logística</p>
        </div>
        <button
          data-testid="dashboard-registrar-ocorrencia-btn"
          onClick={() => navigate("/ocorrencias?novo=1")}
          className="bg-[#2DC26B] hover:bg-[#22A85B] text-white font-semibold px-4 py-2.5 rounded-lg shadow-sm flex items-center gap-2 transition-colors w-fit"
        >
          <Plus className="w-4 h-4" /> Registrar Ocorrência
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.id} data-testid={k.id} className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm hover:border-[#0F6B3A]/30 transition-all p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold text-[#5A6B62] uppercase tracking-wide">{k.title}</p>
                <p className="text-3xl font-extrabold text-[#0F2A1F] mt-2">{k.value}</p>
                <p className="text-xs text-[#8A9A90] mt-1">{k.sub}</p>
              </div>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${k.color}18` }}>
                <k.icon className="w-5 h-5" style={{ color: k.color }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-5">
          <h2 className="text-base font-semibold text-[#0F2A1F] mb-4">Evolução Diária de Ocorrências (30 dias)</h2>
          <div className="h-64" data-testid="dashboard-chart-30d">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.chart_30d} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradBlue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0F6B3A" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#0F6B3A" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="data" tick={{ fontSize: 10, fill: "#8A9A90" }} tickFormatter={(d) => d.slice(8, 10)} />
                <YAxis tick={{ fontSize: 10, fill: "#8A9A90" }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D9E3DC", borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="total" name="Ocorrências" stroke="#0F6B3A" strokeWidth={2.5} fill="url(#gradBlue)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-5">
          <h2 className="text-base font-semibold text-[#0F2A1F] mb-4">Ranking de Ocorrências por Marca</h2>
          <div className="h-64" data-testid="dashboard-chart-marcas">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.ranking_marca} layout="vertical" margin={{ top: 0, right: 28, left: 8, bottom: 0 }}>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: "#8A9A90" }} allowDecimals={false} />
                <YAxis type="category" dataKey="marca" width={110} tick={{ fontSize: 10, fill: "#5A6B62" }} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D9E3DC", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="total" name="Ocorrências" radius={[0, 4, 4, 0]}>
                  {stats.ranking_marca.map((r) => (
                    <Cell key={r.marca} fill={BRAND_COLORS[r.marca] || "#0F6B3A"} />
                  ))}
                  <LabelList dataKey="total" position="right" style={{ fontSize: 11, fill: "#0F2A1F", fontWeight: 700 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recentes */}
      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-[#F1F5F2] flex items-center justify-between">
          <h2 className="text-base font-semibold text-[#0F2A1F]">Ocorrências Recentes</h2>
          <button data-testid="dashboard-ver-todas-btn" onClick={() => navigate("/ocorrencias")}
            className="text-sm font-medium text-[#0F6B3A] hover:underline">Ver todas</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-sm" data-testid="dashboard-recent-table">
            <thead>
              <tr className="bg-[#F4F8F5] border-b border-[#D9E3DC] text-xs font-bold text-[#0F2A1F] uppercase tracking-wider">
                <th className="text-left py-3 px-2 sm:px-4">FAC #</th>
                <th className="text-left py-3 px-2 sm:px-4">Marca</th>
                <th className="text-left py-3 px-2 sm:px-4">Tipo</th>
                <th className="hidden sm:table-cell text-left py-3 px-4">Loja</th>
                <th className="hidden sm:table-cell text-left py-3 px-4">UF</th>
                <th className="hidden sm:table-cell text-left py-3 px-4">Status</th>
                <th className="hidden sm:table-cell text-left py-3 px-4">Data</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent.map((o) => (
                <tr key={o.id} className="border-b border-[#F1F5F2] hover:bg-[#F4F8F5]/80 transition-colors">
                  <td className="py-3 px-2 sm:px-4 font-mono text-xs font-semibold text-[#0F2A1F] truncate">{o.fac}</td>
                  <td className="py-3 px-2 sm:px-4 text-[#5A6B62] truncate">{o.marca}</td>
                  <td className="py-3 px-2 sm:px-4 text-[#5A6B62] truncate">{o.tipo}</td>
                  <td className="hidden sm:table-cell py-3 px-4 text-[#5A6B62]">{o.loja}</td>
                  <td className="hidden sm:table-cell py-3 px-4 text-[#5A6B62]">{o.uf}</td>
                  <td className="hidden sm:table-cell py-3 px-4"><StatusBadge status={o.status} /></td>
                  <td className="hidden sm:table-cell py-3 px-4 text-[#8A9A90] text-xs">{o.data}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
